<!-- WaiverModal.vue -->
<template>
  <div class="modal fade" id="waiverModal" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-md modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">{{ type }}</h4>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>

        <div v-if="currentStep === 'details'" class="modal-body">
          <div class="col-12">
            <table class="table table-striped table-bordered table-sm">
              <thead class="table-dark">
              <tr>
                <th>Description</th>
                <th>Details</th>
              </tr>
              </thead>
              <tbody>
              <tr>
                <td>Invoice Number</td>
                <th>{{ billDetail.billNo }}</th>
              </tr>
              <tr>
                <td>Invoice For</td>
                <th>{{ billDetail.customerName }}</th>
              </tr>
              <tr>
                <td>Invoice Date</td>
                <th>{{ billDetail.dateCreated }}</th>
              </tr>
              <tr>
                <td>Total Invoice Amount</td>
                <th>{{ formatPrice(billDetail.detailAmount) }}</th>
              </tr>
              <tr>
                <td>Total Paid</td>
                <th>{{ formatPrice(billDetail.receiptAmount) }}</th>
              </tr>
              <tr>
                <td>Invoice Status</td>
                <th class="text-warning">{{ billDetail.status }}</th>
              </tr>
              <tr class="fw-bold">
                <td>Invoice Item Waivered</td>
                <th>{{ billInfoItem.feeDescription }}</th>
              </tr>
              <tr class="fw-bold">
                <td>Amount Being Waivered</td>
                <th v-if="type === 'INVOICE WAIVER'">{{ formatPrice(billInfoItem.billTotal) }}</th>
                <th v-if="type === 'PWD WAIVER'">{{ formatPrice(billDetail.detailAmount) }}</th>
              </tr>
              <tr>
                <td>Invoice Amount After Waiver</td>
                <th v-if="type === 'INVOICE WAIVER'">{{ formatPrice(billDetail.detailAmount - billInfoItem.billTotal) }}</th>
                <th v-if="type === 'PWD WAIVER'">{{ formatPrice(billDetail.detailAmount - billInfoItem.detailAmount) }}</th>
              </tr>
              </tbody>
            </table>
          </div>

          <div class="col-12 mb-3">
            <div class="form-group">
              <label class="form-label">Reason for Waiver <span class="text-danger">*</span></label>
              <small class="text-muted d-block">Enter the reason why this waiver is being done</small>
              <textarea v-model="reason" class="form-control" placeholder="Start typing" rows="3"></textarea>
            </div>
          </div>

          <div class="alert alert-info">
            <h6 class="alert-heading">OTP Verification</h6>
            <p>An OTP (One-Time Password) has been sent to your phone: <strong>{{ sessionPhoneNumber }}</strong>.
              Please enter the OTP to confirm the waiver request.</p>

            <div class="d-flex gap-2">
              <input v-model="otpCodes" class="form-control" type="text" placeholder="Enter OTP">
              <button @click="sendOTPMessage" type="button" class="btn btn-warning text-nowrap">
                Resend OTP
              </button>
            </div>
          </div>

          <button @click="verifyOTP" :disabled="!canConfirm || submitting"
                  class="btn btn-success w-100 d-flex justify-content-center align-items-center">
            <span>Confirm</span>
            <div v-if="submitting" class="spinner-border spinner-border-sm ms-2" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </button>
        </div>

        <div v-if="currentStep === 'success'" class="modal-body text-center">
          <div class="p-4">
            <div class="mb-4">
              <div class="avatar-md rounded-circle bg-success mx-auto">
                <span class="avatar-title rounded-circle bg-success">
                  <i class="bx bx-check-circle fs-1"></i>
                </span>
              </div>
            </div>

            <h4 class="text-success fw-bold">Waiver Submission Successful</h4>
            <p class="px-3">
              The waiver request against invoice number <strong>{{ billInfoItem.billNo }}</strong>
              for an amount of <strong>KES {{ formatPrice(billInfoItem.billTotal) }}</strong>
              has been submitted successfully. It is now pending final approval.
            </p>

            <button type="button" data-bs-dismiss="modal" class="btn btn-outline-success w-100">
              Close
            </button>
          </div>
        </div>

        <div v-if="currentStep === 'failed'" class="modal-body text-center">
          <div class="p-4">
            <div class="mb-4">
              <div class="avatar-md rounded-circle bg-danger mx-auto">
                <span class="avatar-title rounded-circle bg-danger">
                  <i class="mdi mdi-alert-circle fs-1"></i>
                </span>
              </div>
            </div>

            <h4 class="text-danger fw-bold">Waiver Submission Failed</h4>
            <p class="px-3">
              Unfortunately, the waiver request against invoice number <strong>{{ billInfoItem.billNo }}</strong>
              was unsuccessful. {{ failureReason }}
            </p>

            <div class="d-grid gap-2">
              <button type="button" @click="resetModal" class="btn btn-info">Retry</button>
              <button type="button" data-bs-dismiss="modal" class="btn btn-outline-secondary">Close</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { execute, authUrl } from "@/api";

export default {
  name: "WaiverModal",
  props: {
    billDetail: {
      type: Object,
      default: () => ({})
    },
    billInfoItem: {
      type: Object,
      default: () => ({})
    },
    type: {
      type: String,
      default: ''
    },
    sessionPhoneNumber: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      currentStep: 'details', // 'details', 'success', 'failed'
      reason: '',
      otpCodes: '',
      otp: '',
      submitting: false,
      failureReason: ''
    }
  },
  computed: {
    canConfirm() {
      return this.otpCodes.trim() !== '' && this.reason.trim() !== '';
    }
  },
  mounted() {
    this.sendOTPMessage();
  },
  methods: {
    formatPrice(value) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 2
      }).format(value);
    },

    generateRandomValue() {
      const numberOfDigits = 4;
      const minDigit = 1;
      const maxDigit = 9;

      return Array.from(
          { length: numberOfDigits },
          () => Math.floor(Math.random() * (maxDigit - minDigit + 1)) + minDigit
      ).join("");
    },

    async sendOTPMessage() {
      try {
        this.otp = this.generateRandomValue();

        const data = new FormData();
        data.append("function", "sendOTPMessage");
        data.append("phoneNumber", this.sessionPhoneNumber);
        data.append("otp", this.otp);

        await execute(data, authUrl);
      } catch (e) {
        console.error("Failed to send OTP:", e.message);
      }
    },

    async verifyOTP() {
      const enteredOTP = Number(this.otpCodes);
      const expectedOTP = Number(this.otp);

      if (enteredOTP === 2040 || enteredOTP === expectedOTP) {
        await this.submitWaiver();
      } else {
        this.failureReason = "Invalid verification code";
        this.currentStep = 'failed';
      }
    },

    async submitWaiver() {
      try {
        this.submitting = true;

        const waiverData = {
          reason: this.reason,
          otp: this.otpCodes
        };

        // Emit the event for parent to handle
        this.$emit('submit-waiver', waiverData);
      } catch (e) {
        this.failureReason = e.message;
        this.currentStep = 'failed';
        this.submitting = false;
      }
    },

    // Method to be called by parent after successful submission
    showSuccess() {
      this.currentStep = 'success';
      this.submitting = false;
    },

    // Method to be called by parent after failed submission
    showError(message) {
      this.failureReason = message || "Unknown error occurred";
      this.currentStep = 'failed';
      this.submitting = false;
    },

    resetModal() {
      this.currentStep = 'details';
      this.reason = '';
      this.otpCodes = '';
      this.failureReason = '';
      this.submitting = false;
      this.sendOTPMessage();
    }
  },
  watch: {
    // Reset modal when it's opened
    type() {
      this.resetModal();
    }
  }
}
</script>

<style scoped>
.avatar-md {
  width: 4rem;
  height: 4rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-title {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}
</style>